import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FiArrowRight } from "react-icons/fi";
import { Icon } from "@iconify/react";
import { pageTitle } from "../../helpers/PageTitle";
import { siteContact } from "../../data/siteContact";
import { Select } from "../ui/select";
import { servicesData } from "../../data/servicesData";
import { useForm, ValidationError } from "@formspree/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";

const features = [
  {
    icon: "mdi:flash-outline",
    title: "Fast Response",
    desc: "We reply within 24 hours",
  },
  {
    icon: "mdi:account-group-outline",
    title: "Expert Team",
    desc: "Skilled professionals at your service",
  },
  {
    icon: "mdi:shield-check-outline",
    title: "Trusted Partner",
    desc: "Your success is our priority",
  },
];

// faqs data removed - was unused (FAQ section is commented out). Restore when FAQ section is re-enabled.

export default function ContactPage() {
  pageTitle("Contact");

  const [service, setService] = useState("");
  const [consent, setConsent] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [state, handleSubmit] = useForm("xkjnrdpv");

  const onSubmit = (e) => {
    if (!consent) {
      e.preventDefault();
      setConsentError(true);
      return;
    }
    setConsentError(false);
    handleSubmit(e);
  };

  return (
    <main className="relative overflow-hidden bg-[#fbfdfd]">
      {/* Hero Section - Two Column Layout */}
      <section className="relative z-10 px-4 pt-[150px] pb-16 sm:px-6 max-[991px]:pt-[120px]">
        <div className="mx-auto max-w-[1200px] lg:flex lg:gap-16 lg:items-start">
          {/* Left Side */}
          <div className="mb-12 lg:mb-0 lg:w-[52%] lg:sticky lg:top-28">
            <div className="mb-5 inline-flex items-center gap-3">
              <span className="text-[11px] font-bold uppercase tracking-[3px] text-[#1b667a]">
                Contact Us
              </span>
              <div className="h-[2px] w-10 bg-[#1b667a]" />
            </div>

            <h1 className="mb-6 text-[clamp(32px,4vw,42px)] font-bold leading-[1.15] text-gray-900">
              Let's create something extraordinary{" "}
              <span className="text-[#1b667a]">together.</span>
            </h1>

            <p className="mb-8 max-w-[400px] text-[15px] leading-[1.7] text-gray-500">
              Have a project in mind or need expert guidance? We'd love to hear
              from you and help you bring your ideas to life.
            </p>

            {/* Features */}
            <div className="mb-10 grid grid-cols-3 gap-5">
              {features.map((f, i) => (
                <div key={i} className="text-center">
                  <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-[#1b667a]/10">
                    <Icon icon={f.icon} className="h-5 w-5 text-[#1b667a]" />
                  </div>
                  <h4 className="mb-1 font-medium text-[18px] text-gray-900">
                    {f.title}
                  </h4>
                  <p className="text-[14px] leading-[1.5] text-gray-400">
                    {f.desc}
                  </p>
                </div>
              ))}
            </div>

            {/* Visit Card */}

            <div className="relative overflow-hidden rounded-[20px] border border-[#E4EEEE] bg-white p-6 shadow-[0_8px_30px_rgba(27,102,122,0.06)]">
              {/* Decorative dots */}
              <div
                className="pointer-events-none absolute right-5 top-5 h-16 w-20 opacity-[0.18]"
                style={{
                  backgroundImage:
                    "radial-gradient(#1b667a 1.2px, transparent 1.2px)",
                  backgroundSize: "8px 8px",
                }}
              />

              {/* Heading */}
              <div className="relative mb-6">
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#1b667a]">
                  Contact Info
                </p>

                <h3 className="text-[24px] font-medium tracking-[-0.02em] text-gray-900">
                  Come & visit{" "}
                  <span className="text-[#1b667a]">our place!</span>
                </h3>
              </div>

              {/* Contact Details */}
              <div className="mt-4">
                {/* Email */}
                <div className="flex items-center gap-4 pb-2">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#EAF7F5]">
                    <Icon
                      icon="mdi:email-outline"
                      className="h-[19px] w-[19px] text-[#1b667a]"
                    />
                  </div>

                  <div className="min-w-0">
                    <p className="mb-1 text-[14px] font-semibold tracking-wide text-gray-400">
                      Email
                    </p>
                    <a
                      href={siteContact.emailHref}
                      className="text-[17px] font-medium text-gray-700 hover:text-[#1b667a]"
                    >
                      {siteContact.email}
                    </a>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-center gap-6 ">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#EAF7F5]">
                    <Icon
                      icon="mdi:phone-outline"
                      className="h-[19px] w-[19px] text-[#1b667a]"
                    />
                  </div>

                  <div>
                    <p className="mb-1 text-[14px] font-semibold tracking-wide text-gray-400">
                      Phone
                    </p>
                    <a
                      href={siteContact.phoneHref}
                      className="text-[17px] font-medium text-gray-700 hover:text-[#1b667a]"
                    >
                      {siteContact.phone}
                    </a>
                  </div>
                </div>

                {/* Address */}
                <div className="flex items-start gap-6 mt-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#EAF7F5]">
                    <Icon
                      icon="mdi:map-marker-outline"
                      className="h-[19px] w-[19px] text-[#1b667a]"
                    />
                  </div>

                  <div>
                    <p className="mb-1 text-[14px] font-semibold tracking-wide text-gray-400">
                      Address
                    </p>

                    <p className="text-[17px] font-medium leading-5 text-gray-700">
                      D-166/2 D Block Sector 50
                    </p>

                    <p className="text-[13px] leading-5 text-gray-500">
                      Noida, 201301, India
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="lg:w-[58%] mt-8">
            <div className="rounded-[20px] border border-gray-100 bg-white p-8 shadow-[0_8px_40px_rgba(0,0,0,0.06)] sm:p-10">
              <div className="mb-8 flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#1b667a]">
                  <Icon
                    icon="mdi:send-outline"
                    className="h-5 w-5 text-white"
                  />
                </div>
                <div>
                  <h3 className="text-[20px] font-bold text-gray-900">
                    Send us a message
                  </h3>
                  <p className="text-[13px] text-gray-400">
                    Fill out the form below and we'll get back to you soon.
                  </p>
                </div>
              </div>

              {state.succeeded ? (
                <div className="flex min-h-[350px] flex-col items-center justify-center text-center animate-[fadeIn_0.4s_ease]">
                  <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[#EAF7F5]">
                    <Icon icon="mdi:check" className="h-8 w-8 text-[#1b667a]" />
                  </div>

                  <h3 className="mb-2 text-2xl font-bold text-gray-900">
                    Message Sent Successfully
                  </h3>

                  <p className="max-w-md text-sm leading-6 text-gray-500">
                    Thank you for reaching out. We've received your message and
                    will get back to you shortly.
                  </p>
                  <button
                    type="button"
                    onClick={() => window.location.reload()}
                    className="mt-6 rounded-full border border-gray-200 bg-white px-6 py-2.5 text-sm font-semibold text-gray-700 transition hover:border-[#1b667a] hover:text-[#1b667a]"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <>
                  {state.errors && state.errors.length > 0 && (
                    <div className="mb-2 rounded-[10px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                      {state.errors.getFormErrors().map((e, i) => (
                        <p key={i}>
                          {e.message ||
                            "Something went wrong. Please try again."}
                        </p>
                      ))}
                      {state.errors.getAllFieldErrors().length > 0 &&
                        state.errors.getFormErrors().length === 0 && (
                          <p>Please check the fields below.</p>
                        )}
                    </div>
                  )}
                  <form onSubmit={onSubmit} className="space-y-5" noValidate>
                    <div>
                      <label className="mb-2 block text-[13px] font-semibold text-gray-700">
                        Your full name
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          name="name"
                          required
                          placeholder="Enter your full name"
                          className="w-full rounded-[10px] border border-gray-200 bg-gray-50 px-4 py-3.5 pr-12 text-[14px] text-gray-700 outline-none transition-all duration-300 focus:border-[#1b667a] focus:bg-white focus:ring-2 focus:ring-[#1b667a]/10"
                        />
                        <Icon
                          icon="mdi:account-outline"
                          className="absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-300"
                        />
                      </div>
                      <ValidationError
                        prefix="Name"
                        field="name"
                        errors={state.errors}
                        className="mt-1 text-xs text-red-600"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-[13px] font-semibold text-gray-700">
                        Email address
                      </label>
                      <div className="relative">
                        <input
                          type="email"
                          name="email"
                          required
                          placeholder="Enter your email address"
                          className="w-full rounded-[10px] border border-gray-200 bg-gray-50 px-4 py-3.5 pr-12 text-[14px] text-gray-700 outline-none transition-all duration-300 focus:border-[#1b667a] focus:bg-white focus:ring-2 focus:ring-[#1b667a]/10"
                        />
                        <Icon
                          icon="mdi:email-outline"
                          className="absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-300"
                        />
                      </div>
                      <ValidationError
                        prefix="Email"
                        field="email"
                        errors={state.errors}
                        className="mt-1 text-xs text-red-600"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-[13px] font-semibold text-gray-700">
                        Phone number
                      </label>
                      <div className="relative">
                        <input
                          type="tel"
                          name="phone"
                          placeholder="Enter your phone number"
                          className="w-full rounded-[10px] border border-gray-200 bg-gray-50 px-4 py-3.5 pr-12 text-[14px] text-gray-700 outline-none transition-all duration-300 focus:border-[#1b667a] focus:bg-white focus:ring-2 focus:ring-[#1b667a]/10"
                        />
                        <Icon
                          icon="mdi:phone-outline"
                          className="absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-300"
                        />
                      </div>
                      <ValidationError
                        prefix="Phone"
                        field="phone"
                        errors={state.errors}
                        className="mt-1 text-xs text-red-600"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-[13px] font-semibold text-gray-700">
                        Service interested in
                      </label>
                      <Select
                        placeholder="Select a service"
                        value={service}
                        onValueChange={setService}
                        options={servicesData.map(({ slug, title }) => ({
                          value: slug,
                          label: title,
                        }))}
                      />
                      {/* Hidden input so Formspree actually receives the service value */}
                      <input type="hidden" name="service" value={service} />
                      <ValidationError
                        prefix="Service"
                        field="service"
                        errors={state.errors}
                        className="mt-1 text-xs text-red-600"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-[13px] font-semibold text-gray-700">
                        Project brief
                      </label>
                      <div className="relative">
                        <textarea
                          rows={5}
                          name="message"
                          required
                          placeholder="Tell us about your project, requirements or ideas..."
                          className="w-full resize-none rounded-[10px] border border-gray-200 bg-gray-50 px-4 py-3.5 pr-12 text-[14px] text-gray-700 outline-none transition-all duration-300 focus:border-[#1b667a] focus:bg-white focus:ring-2 focus:ring-[#1b667a]/10"
                        />
                        <Icon
                          icon="mdi:pencil-outline"
                          className="absolute bottom-4 right-4 h-5 w-5 text-gray-300"
                        />
                      </div>
                      <ValidationError
                        prefix="Message"
                        field="message"
                        errors={state.errors}
                        className="mt-1 text-xs text-red-600"
                      />
                    </div>

                    {/* Consent Checkbox */}
                    <div>
                      <label className="flex cursor-pointer items-start gap-3">
                        <input
                          type="checkbox"
                          name="consent"
                          checked={consent}
                          onChange={(e) => {
                            setConsent(e.target.checked);
                            if (e.target.checked) setConsentError(false);
                          }}
                          required
                          aria-required="true"
                          aria-invalid={consentError}
                          className="mt-0.5 h-[18px] w-[18px] shrink-0 cursor-pointer appearance-none rounded-[4px] border border-gray-300 bg-gray-50 checked:border-[#1b667a] checked:bg-[#1b667a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1b667a]/30 focus-visible:ring-offset-0"
                          style={
                            consent
                              ? {
                                  backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M3 7L5.5 9.5L11 3.5' stroke='white' stroke-width='1.7' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                                  backgroundRepeat: "no-repeat",
                                  backgroundPosition: "center",
                                  backgroundSize: "12px 12px",
                                }
                              : undefined
                          }
                        />
                        <span className="text-[13px] leading-[1.6] text-gray-600">
                          I consent to the processing of my data as per the{" "}
                          <a
                            href="#privacy-policy"
                            onClick={(event) => {
                              event.preventDefault();
                              setPrivacyOpen(true);
                            }}
                            className="font-semibold text-[#1b667a] underline underline-offset-2 hover:text-[#155568]"
                          >
                            Privacy Policy
                          </a>{" "}
                          and agree to be contacted regarding my inquiry.
                          <span className="ml-0.5 text-red-500">*</span>
                        </span>
                      </label>
                      {consentError && (
                        <p className="mt-1.5 pl-[30px] text-xs text-red-600">
                          You must agree to the privacy policy before
                          submitting.
                        </p>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={state.submitting || !consent}
                      title={
                        !consent
                          ? "Please accept the consent checkbox to submit"
                          : undefined
                      }
                      className="group flex w-full items-center justify-center gap-2 rounded-[10px] bg-[#1b667a] px-8 py-3 text-[14px] font-semibold text-white shadow-lg shadow-[#1b667a]/20 transition-all duration-300 hover:bg-[#155568] hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-[#1b667a] disabled:hover:shadow-lg"
                    >
                      {state.submitting ? "Sending..." : "Send Message"}
                      {!state.submitting && (
                        <FiArrowRight
                          className="transition-transform duration-300 group-hover:translate-x-1"
                          size={16}
                        />
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      {/* <section className="relative z-10 px-4 sm:px-6 pb-20">
        <div className="mx-auto max-w-[700px]">
          <div className="mb-10 text-center">
            <div className="mb-4 inline-flex items-center gap-2.5">
              <div className="h-[2px] w-8 bg-[#1b667a]" />
              <span className="text-[11px] font-bold uppercase tracking-[3px] text-[#1b667a]">FAQ</span>
              <div className="h-[2px] w-8 bg-[#1b667a]" />
            </div>
            <h2 className="text-[clamp(24px,3vw,36px)] font-bold text-gray-900">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, i) => {
              const isOpen = openFaq === i;
              return (
                <div
                  key={i}
                  className={`rounded-[14px] border transition-all duration-300 ${
                    isOpen
                      ? "border-[#1b667a]/20 bg-white shadow-[0_4px_20px_rgba(27,102,122,0.08)]"
                      : "border-gray-100 bg-white shadow-[0_1px_4px_rgba(0,0,0,0.02)]"
                  }`}
                >
                  <button
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    className="flex w-full items-center gap-4 p-5 text-left"
                  >
                    <div
                      className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full transition-all duration-300 ${
                        isOpen
                          ? "bg-[#1b667a] text-white"
                          : "bg-gray-100 text-gray-400"
                      }`}
                    >
                      <Icon
                        icon="mdi:plus"
                        className={`h-4 w-4 transition-transform duration-300 ${
                          isOpen ? "rotate-45" : ""
                        }`}
                      />
                    </div>
                    <span
                      className={`text-[15px] font-medium transition-colors duration-300 ${
                        isOpen ? "text-[#1b667a]" : "text-gray-700"
                      }`}
                    >
                      {faq.q}
                    </span>
                  </button>
                  <div
                    className="overflow-hidden transition-all duration-400 ease-[cubic-bezier(0.4,0,0.2,1)]"
                    style={{
                      maxHeight: isOpen ? "200px" : "0px",
                      opacity: isOpen ? 1 : 0,
                    }}
                  >
                    <div className="px-5 pb-5 pl-[72px] text-[14px] leading-[1.7] text-gray-500">
                      {faq.a}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section> */}

      {/* Map */}
      {/* <section className="relative z-10 px-4 sm:px-6 pb-20">
        <div className="mx-auto max-w-[1100px] overflow-hidden rounded-[20px] border border-gray-100 shadow-[0_4px_24px_rgba(0,0,0,0.06)]">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3502.5690709639936!2d77.06849999999999!3d28.63042!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390d1d40a2d2f3e1%3A0x7e3b2e1c8e7d3f5a!2sSector%2050%2C%20Noida%2C%20Uttar%20Pradesh!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin"
            width="100%"
            height="400"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            title="Office Location"
          />
        </div>
      </section> */}

      {/* CTA */}
      <section className="relative z-10 px-4 sm:px-6 pb-20">
        <div className="relative mx-auto max-w-[1000px] overflow-hidden rounded-[24px] border border-gray-100 bg-white px-8 py-16 shadow-[0_8px_40px_rgba(0,0,0,0.06)] sm:px-16">
          <div className="pointer-events-none absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-transparent via-[#1b667a]/20 to-transparent" />
          <div className="pointer-events-none absolute right-0 top-0 h-full w-1 bg-gradient-to-b from-transparent via-[#F5A623]/20 to-transparent" />
          <div
            className="pointer-events-none absolute left-[3%] top-[8%] h-24 w-32 opacity-[0.08]"
            style={{
              backgroundImage: "radial-gradient(#1b667a 1px, transparent 1px)",
              backgroundSize: "10px 10px",
            }}
          />
          <div
            className="pointer-events-none absolute bottom-[8%] right-[3%] h-20 w-28 opacity-[0.08]"
            style={{
              backgroundImage: "radial-gradient(#F5A623 1px, transparent 1px)",
              backgroundSize: "10px 10px",
            }}
          />

          <div className="relative text-center">
            <h2 className="mb-4 text-[clamp(26px,3.5vw,38px)] font-bold leading-[1.15] text-gray-900">
              Ready to build something{" "}
              <span className="text-[#1b667a]">amazing?</span>
            </h2>
            <p className="mx-auto mb-8 max-w-[520px] text-[16px] leading-[1.8] text-gray-500">
              Let's turn your vision into reality. Our team is ready to help you
              scale your business with cutting-edge technology.
            </p>
            <Link
              to="/service"
              className="group inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-8 py-3.5 text-[14px] font-semibold text-gray-700 transition-all duration-300 hover:border-[#1b667a] hover:text-[#1b667a] hover:shadow-md"
            >
              Explore Our Services
              <FiArrowRight
                className="transition-transform duration-300 group-hover:translate-x-1"
                size={16}
              />
            </Link>
          </div>
        </div>
      </section>
      {/* Privacy Policy Modal - simple & readable */}
      <Dialog open={privacyOpen} onOpenChange={setPrivacyOpen}>
        <DialogContent className="max-w-[620px] max-h-[88vh] rounded-[22px] border-[#1b667a]/15 shadow-[0_24px_80px_rgba(15,23,42,0.2)]">
          <DialogHeader className="border-b border-[#1b667a] bg-[#1b667a] px-6 pb-3 pt-5 sm:px-8">
            <DialogTitle className="text-[22px] sm:text-[24px]">
              <span className="text-white">Privacy</span>{" "}
              <span className="text-[#F5A623]">Policy</span>
            </DialogTitle>
            <DialogDescription className="-mt-3 max-w-[500px] text-[15px] leading-5 text-white/80 [&_span]:text-white">
              How <span className="font-medium text-gray-700">Algostine</span>{" "}
              handles your data on algostine.com — short and clear.
            </DialogDescription>
          </DialogHeader>

          <DialogBody className="space-y-6 px-6 py-6 text-[15px] leading-7 sm:px-8">
            <p className="rounded-xl border border-[#1b667a]/10 bg-[#1b667a]/[0.04] px-4 py-3.5 text-[14px] leading-6 text-gray-600">
              At{" "}
              <strong className="font-semibold text-gray-900">Algostine</strong>{" "}
              — D-166/2, D Block, Sector 50, Noida 201301, India — we respect
              your privacy.
              <br />
              <a
                href={siteContact.emailHref}
                className="font-medium text-[#1b667a] underline underline-offset-4"
              >
                {siteContact.email}
              </a>{" "}
              <span className="text-gray-400">·</span> +91 98185 84043
            </p>

            <div>
              <h4 className="mb-2 text-[16px] font-semibold text-gray-900">
                What we collect
              </h4>
              <ul className="list-disc space-y-1 pl-5 marker:text-[#1b667a]/30">
                <li>
                  <span className="font-medium text-gray-700">You share:</span>{" "}
                  name, email, phone, service, project brief/message.
                </li>
                <li>
                  <span className="font-medium text-gray-700">Automatic:</span>{" "}
                  IP, device/browser, and pages viewed in technical logs needed to operate the site.
                </li>
              </ul>
            </div>

            <div>
              <h4 className="mb-2 text-[16px] font-semibold text-gray-900">
                How we use it
              </h4>
              <ul className="list-disc space-y-1 pl-5 marker:text-[#1b667a]/30">
                <li>Reply to your inquiry and deliver requested services.</li>
                <li>Improve our site/services and meet legal obligations.</li>
                <li>No spam — marketing only if you opt in.</li>
              </ul>
            </div>

            <div>
              <h4 className="mb-2 text-[16px] font-semibold text-gray-900">
                Sharing & cookies
              </h4>
              <p>
                We{" "}
                <strong className="font-semibold text-gray-900">
                  never sell
                </strong>{" "}
                your data. Shared only with{" "}
                <strong className="font-medium text-gray-900">Formspree</strong>{" "}
                (secure form delivery), hosting and analytics providers when
                required to operate the site, and when required by law. We do
                not use advertising cookies or ad tracking.
              </p>
            </div>

            <div>
              <h4 className="mb-2 text-[16px] font-semibold text-gray-900">
                Retention & rights
              </h4>
              <p>
                Kept{" "}
                <strong className="font-medium text-gray-900">
                  up to 24 months
                </strong>{" "}
                then deleted/anonymized. Protected by HTTPS, access controls and
                encryption. You can request access, correction or deletion
                anytime at{" "}
                <a
                  href={siteContact.emailHref}
                  className="font-medium text-[#1b667a] underline underline-offset-4"
                >
                  {siteContact.email}
                </a>{" "}
                (subject “Privacy Request”, reply within 30 days).
              </p>
            </div>

            <p className="rounded-xl border border-amber-200/60 bg-amber-50/60 px-4 py-3 text-center text-[13px] leading-6 text-gray-600">
              By checking the consent box you agree to this processing for your
              inquiry. You can withdraw anytime.
            </p>
          </DialogBody>

          <DialogFooter className="border-t border-[#1b667a]/10 bg-[#1b667a]/[0.035] px-6 py-4 sm:px-8">
            <span className="hidden text-xs leading-5 text-gray-500 sm:block">
              Last updated: Sep 4, 2026 •{" "}
              <span className="font-medium text-[#1b667a]">DPDP & GDPR</span>
            </span>
            <div className="flex w-full gap-2 sm:w-auto">
              <Button
                variant="secondary"
                className="flex-1 sm:flex-none rounded-[10px] border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 shadow-none"
                onClick={() => setPrivacyOpen(false)}
              >
                Close
              </Button>
              <Button
                variant="brand"
                className="flex-1 sm:flex-none rounded-[10px]"
                onClick={() => {
                  setConsent(true);
                  setConsentError(false);
                  setPrivacyOpen(false);
                }}
              >
                I understand
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}
